import React, { useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { api, ApiError } from "../lib/api";

interface BookDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/*
 * Velaigal demo-booking modal. Reached from "Book a demo" on the landing
 * page. Follows the spec §4.7 modal: white container (the only place
 * white is allowed), ink border, hairline-divided sections, saffron
 * focus rings via the .input class.
 */
export default function BookDemoModal({ isOpen, onClose }: BookDemoModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.company) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await api("/api/demo-requests", {
        method: "POST",
        body: {
          name: formData.name,
          email: formData.email,
          company: formData.company,
          message:
            [formData.phone ? `Phone: ${formData.phone}` : null, formData.message]
              .filter(Boolean)
              .join("\n") || undefined,
        },
      });
      toast.success("Demo request received. We will be in touch shortly.");
      setFormData({ name: "", email: "", company: "", phone: "", message: "" });
      onClose();
    } catch (error) {
      const err = error instanceof ApiError ? error : new Error("Something went wrong");
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(26, 22, 20, 0.32)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="book-demo-title"
    >
      <div
        className="bg-white border border-ink rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-modal animate-modalIn"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-hairline flex items-start justify-between gap-6">
          <div>
            <span className="label-editorial">Book a demo</span>
            <h2
              id="book-demo-title"
              className="font-display text-2xl text-ink mt-3 leading-snug"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
            >
              Tell us about your hiring.
            </h2>
            <p className="mt-2 text-sm text-ink-tertiary leading-relaxed">
              We will reply within one business day with a 30-minute slot.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-tertiary hover:text-saffron transition-colors mt-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-ink mb-2"
            >
              Full name
              <span className="text-saffron ml-1" aria-hidden="true">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="input"
              required
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-ink mb-2"
            >
              Work email
              <span className="text-saffron ml-1" aria-hidden="true">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="input"
              required
            />
          </div>

          <div>
            <label
              htmlFor="company"
              className="block text-sm font-medium text-ink mb-2"
            >
              Company
              <span className="text-saffron ml-1" aria-hidden="true">*</span>
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              className="input"
              required
            />
          </div>

          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-ink mb-2"
            >
              Phone
              <span className="text-ink-tertiary text-xs ml-2 font-normal">
                Optional
              </span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="input"
            />
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-ink mb-2"
            >
              What would you like to cover?
              <span className="text-ink-tertiary text-xs ml-2 font-normal">
                Optional
              </span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={3}
              value={formData.message}
              onChange={handleInputChange}
              className="input resize-y"
              placeholder="Roles you are hiring for, team size, anything specific."
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary"
            >
              {isSubmitting ? "Sending..." : "Request a slot"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
